import axios from 'axios';
import _ from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import uuid from 'uuid/v4';
import { Base64 } from 'js-base64';
import { fetchUser } from './auth';
import { buildProfileUpdate } from '../common/lib/buildProfilePayloads';

const constructBaseUrl = () => {
  return __CONFIG__.apiBaseUrl;
};

const generateGoogleApiSessionToken = () => {
  return uuid();
};

const baseUrl = constructBaseUrl();

const instantiateAxios = (baseURL) => {
  const headers = { 'Content-Type': 'application/json' };
  return axios.create({
    baseURL,
    headers,
    timeout: 30000,
    crossDomain: true,
    withCredentials: false,
    validateStatus: () => true,
  });
};

const fetchUrlParams = () => {
  const queryParams = queryString.parse(window.location.search);
  return {
    clientId: _.get(queryParams, 'clientId', false),
    secretHash: _.get(queryParams, 'secretHash', false),
  };
};

const create = () => {
  const axiosInstance = instantiateAxios(baseUrl);
  const googlePlacesApiInstance = instantiateAxios(__CONFIG__.googlePlacesApiBaseUrl);
  const trimInitialRegisterPayload = (registerUserForm) => {
    const user = Object.assign({}, registerUserForm);
    const newsletterOptIn = _.get(registerUserForm, 'newsletter', false);
    user.phone = user.phone.replace(/[^\d]/g, '');
    delete user.acceptedTermsAndConditions;
    delete user.confirmPassword;
    delete user.isOver18;
    delete user.newsletter;

    if (!user.middleName) {
      delete user.middleName;
    }
    if (user.suffix) {
      delete user.suffix;
    }
    if (user.birthdate) {
      user.birthdate = moment(user.birthdate).format('MM/DD/YYYY');
    }
    user.newsletterOptIn = newsletterOptIn;
    const urlParams = fetchUrlParams();
    if (urlParams.clientId && urlParams.secretHash) {
      user.clientId = urlParams.clientId;
      user.secretHash = urlParams.secretHash;
    } else if (urlParams.clientId) {
      user.clientId = urlParams.clientId;
    }
    return user;
  };

  const postInitialRegister = ({ registerUserForm }) => {
    const user = trimInitialRegisterPayload(registerUserForm);
    return axiosInstance.post('/sso/register-verify', user, {
      headers: { 'x-api-key': __CONFIG__.apiKey },
    });
  };

  const registerUser = ({ registerUserForm }) => postInitialRegister({ registerUserForm });

  const getRegisterEmailVerify = ({ accessToken, resend = false } = {}) => {
    const user = fetchUser();
    const config = {
      headers: {
        Authorization: accessToken || user.accessToken,
        'x-api-key': __CONFIG__.apiKey,
      },
    };
    if (resend) {
      config.params = { resend: 'true' };
    }
    return axiosInstance.get('/sso/register-email-verify', config);
  };

  const putCompleteRegistration = (body) => {
    const user = fetchUser();
    return axiosInstance.put('/sso/register-verify', body, {
      headers: {
        Authorization: user.accessToken,
        'x-api-key': __CONFIG__.apiKey,
      },
    });
  };

  const putRegisterVerifyWithCode = ({ code, accessToken }) => {
    const user = fetchUser();
    const token = accessToken || user.accessToken;
    return axiosInstance.put(
      '/sso/register-verify',
      { code },
      {
        headers: {
          Authorization: token,
          'x-api-key': __CONFIG__.apiKey,
        },
      },
    );
  };

  const loginUser = ({ loginUserForm }) => {
    const user = Object.assign({}, loginUserForm);
    // IDENTIFIERS VIA 3rd PARTY
    const urlParams = fetchUrlParams();
    if (urlParams.clientId && urlParams.secretHash) {
      user.clientId = urlParams.clientId;
      user.secretHash = urlParams.secretHash;
    } else if (urlParams.clientId) {
      user.clientId = urlParams.clientId;
    }
    return axiosInstance.post('/sso/login', user, { headers: { 'x-api-key': __CONFIG__.apiKey } });
  };

  const refreshUserSession = ({ refreshToken }) => {
    const urlParams = fetchUrlParams();
    const clientId = _.get(urlParams, 'clientId', false);
    if (clientId) {
      return axiosInstance.post(
        '/sso/refresh-token',
        {
          clientId: urlParams.clientId,
          refreshToken,
        },
        { headers: { 'x-api-key': __CONFIG__.apiKey } },
      );
    }
  };

  const resetPassword = ({ forgotPasswordForm }) => {
    const resetPasswordPayload = Object.assign({}, forgotPasswordForm);
    // IDENTIFIERS VIA 3rd PARTY
    const urlParams = fetchUrlParams();
    if (urlParams.clientId && urlParams.secretHash) {
      resetPasswordPayload.clientId = urlParams.clientId;
      resetPasswordPayload.secretHash = urlParams.secretHash;
    } else if (urlParams.clientId) {
      resetPasswordPayload.clientId = urlParams.clientId;
    }
    return axiosInstance.post('/sso/reset-password', resetPasswordPayload, {
      headers: { 'x-api-key': __CONFIG__.apiKey },
    });
  };

  const confirmResetPassword = ({ confirmResetPasswordForm, clientId, secret, email, code }) => {
    const confirmResetPasswordPayload = Object.assign({}, confirmResetPasswordForm);
    confirmResetPasswordPayload.clientId = clientId;
    confirmResetPasswordPayload.secret = secret;
    confirmResetPasswordPayload.email = email;
    confirmResetPasswordPayload.code = code;
    delete confirmResetPasswordPayload.confirmPassword;
    return axiosInstance.post('/sso/confirm-reset-password', confirmResetPasswordPayload, {
      headers: { 'x-api-key': __CONFIG__.apiKey },
    });
  };

  const verifyEmail = ({ clientId, secret, email, code }) => {
    const verifyEmailPayload = {
      clientId,
      secret,
      email,
      code,
    };

    return axiosInstance.put('/sso/email-verify', verifyEmailPayload, {
      headers: { 'x-api-key': __CONFIG__.apiKey },
    });
  };

  const fetchProfile = (accessToken = false) => {
    const user = fetchUser();
    return axiosInstance.get('/users', {
      headers: { Authorization: accessToken || user.accessToken },
    });
  };

  const migrateProfile = ({ migrateProfileForm, accessToken }) => {
    let user;
    let accessTokenFetched;
    if (!accessToken) {
      user = fetchUser();
      accessTokenFetched = user.accessToken;
    }
    const finalToken = accessToken || accessTokenFetched;

    const birthdateRaw = _.get(migrateProfileForm, 'birthdate', false);
    const profileUpdate = buildProfileUpdate(migrateProfileForm, birthdateRaw);

    return axiosInstance.put(
      '/sso/update-user',
      { profileUpdate },
      {
        headers: {
          Authorization: finalToken,
          'x-api-key': __CONFIG__.apiKey,
        },
      },
    );
  };

  const signOut = ({ accessToken }) => {
    let user;
    let accessTokenFetched;
    if (!accessToken) {
      user = fetchUser();
      accessTokenFetched = user.accessToken;
    }

    const signOutData = { accessToken: accessToken || accessTokenFetched };

    const urlParams = fetchUrlParams();
    if (urlParams.clientId) {
      signOutData.clientId = urlParams.clientId;
    }
    return axiosInstance.post('/sso/logout', signOutData, {
      headers: {
        Authorization: accessToken || accessTokenFetched,
        'x-api-key': __CONFIG__.apiKey,
      },
    });
  };

  const updateProfile = ({ updateProfileForm, accessToken }) => {
    let user;
    let accessTokenFetched;
    if (!accessToken) {
      user = fetchUser();
      accessTokenFetched = user.accessToken;
    }
    const finalToken = accessToken || accessTokenFetched;

    const birthdateRaw = _.get(updateProfileForm, 'birthdate', false);
    const profileUpdate = buildProfileUpdate(updateProfileForm, birthdateRaw);

    return axiosInstance.put(
      '/sso/update-user',
      { profileUpdate },
      {
        headers: {
          Authorization: finalToken,
          'x-api-key': __CONFIG__.apiKey,
        },
      },
    );
  };

  const fetchPlaces = ({ input }) => {
    console.log('input in api call', input);
    return googlePlacesApiInstance.get('/json', {
      params: {
        input,
        key: __CONFIG__.googleApiKey,
        sessiontoken: generateGoogleApiSessionToken(),
        types: 'address',
        components: 'country:us',
      },
    });
  };

  const fetchGovIdResponse = ({ code, accessToken }) =>
    putRegisterVerifyWithCode({
      code,
      accessToken,
    });

  const checkGovIdStatus = ({ transactionId, govIdType, accessToken }) => {
    let accessTokenFetched;
    let user;
    if (!accessToken) {
      user = fetchUser();
      accessTokenFetched = user.accessToken;
    }
    // Use the same endpoint as initial code exchange, but with transaction_id for polling
    return axiosInstance.put(
      '/sso/handle-govid-response',
      {
        transaction_id: transactionId,
        govIdType,
      },
      { headers: { Authorization: accessToken || accessTokenFetched } },
    );
  };

  const getGovIdLink = ({ govIdType, linkType, accessToken }) => {
    // Get GovID link via backend to avoid CORS issues
    // Backend will handle getting the IDDW token internally
    let accessTokenFetched;
    let user;
    if (!accessToken) {
      user = fetchUser();
      accessTokenFetched = user.accessToken;
    }
    return axiosInstance.post(
      '/sso/get-govid-link',
      {
        govIdType,
        linkType: linkType || 'browser',
      },
      { headers: { Authorization: accessToken || accessTokenFetched } },
    );
  };

  const getPrefillHint = () => {
    const user = fetchUser();
    return axiosInstance.post('/sso/prefill-login-hint', undefined, {
      headers: {
        Authorization: user.accessToken,
        'x-api-key': __CONFIG__.apiKey,
      },
    });
  };

  return {
    registerUser,
    postInitialRegister,
    getRegisterEmailVerify,
    putCompleteRegistration,
    putRegisterVerifyWithCode,
    loginUser,
    resetPassword,
    confirmResetPassword,
    verifyEmail,
    fetchProfile,
    signOut,
    updateProfile,
    fetchPlaces,
    refreshUserSession,
    migrateProfile,
    fetchGovIdResponse,
    checkGovIdStatus,
    getGovIdLink,
    getPrefillHint,
  };
};

export default { create };
